/**
 * Location Management – full-page workspace for the location hierarchy.
 * Header (title + history + search + draft actions), optional AI review strip,
 * toolbar with counts and type filters, then the tree with an optional details panel.
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Text,
  TextInput,
  Button,
  Stack,
  Group,
  Loader,
  ActionIcon,
  Tooltip,
  Modal,
} from '@mantine/core';
import {
  IconX,
  IconPlus,
  IconSearch,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import { getProjectId } from '../../utils/cookies';
import { getPortalSessionSnapshot } from '../../utils/portalSession';
import { getProjectDisplayName } from '../../utils/projectDisplay';
import { LocationHierarchyTree, countNodes } from '../../location-hierarchy/LocationHierarchyTree';
import type { LocationTreeNode } from '../../api/schedulingApis';
import type { TreeApi } from 'react-arborist';
import {
  fetchAllTasks,
  updateLocation,
  deleteLocation,
  locationClassification,
  reassignLocationToTask,
  locationChat,
} from '../../api/schedulingApis';
import type { LocationChatMessage, LocationChatProposedAction } from '../../api/schedulingApis';
import {
  LocationChatDrawer,
  LOCATION_CHAT_INSET_MIN_VIEWPORT,
  resolveChatWidth,
} from './components/LocationChatDrawer';
import { buildChatPreviewTree, planChatApply } from './logic/locationChatApply';
import { buildChatSuggestions } from './logic/locationChatSuggestions';
import { useLocationsTreeStore } from '../../stores/locationsTreeStore';
import {
  collectDescendantIds,
  collectNodesByName,
  countTreeNodes,
  filterTreeRemoved,
  flattenTreeToUpdatePayloads,
  getIdsSortedByDepthDescending,
} from '../../location-hierarchy/aiReviewUtils';
import { LocationBulkMoveModal } from '../../location-hierarchy/LocationBulkMoveModal';
import { revealLocationInTree } from '../../location-hierarchy/locationTreeNavigation';
import { snapshotLocationSubtree } from '../../location-hierarchy/locationDraftUtils';
import { LocationSelectionEditPanel } from './components/LocationSelectionEditPanel';
import { LocationDraftLeaveConfirmModal } from './components/LocationDraftLeaveConfirmModal';
import { useLocationDraftLeaveGuard } from '../../hooks/useLocationDraftLeaveGuard';
import axios from 'axios';
import { useViewportSize } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  PermissionRestrictedTooltip,
  useCanSchedulerAction,
} from '../../permissions/scheduler';
import aiClassificationIcon from '../../assets/images/Frame (1).svg';
import { useFeatureTipsOptional } from '../feature-tips';
import { ScheduleReadinessBanner } from '../schedule-readiness';
import { prepareDashboardData } from '../dashboard/logic/prepareDashboardData';
import type { TaskSnapshot } from '../../stores/types';
import {
  getDashboardMemoryCache,
  setDashboardMemoryCache,
} from '../../dashboard/dashboardProjectCache';
import { loadTasksFromDB } from '../../stores/db';
import {
  buildTypePillCounts,
  collectLocationTypes,
  filterTreeByLocationType,
  findImmediatePreviousNode,
  findNodeById,
  findNodeSiblingContext,
  findProjectRoot,
  formatLocationTypeLabel,
  hasRootNode,
  isProjectRootNode,
  appendFormerNameAsAlias,
} from './logic/locationManagementUtils';
import {
  canDeleteLocationNode,
  canPlaceLocationType,
  isFloorLocationType,
  normalizeLocationTypeKey,
  resolveLocationTypeInput,
  withFloorNumberMeta,
} from './logic/locationTypeRules';


export interface LocationManagementPageProps {
  /** Optional custom title for the header (used when embedded in a wizard step). */
  titleOverride?: string;
  /** Optional right-side content in the header (e.g., Previous/Next buttons in a wizard). */
  headerRight?: React.ReactNode;
}

/** Typed into the panel to wipe the conversation, with or without a leading slash. */
export const CLEAR_CHAT_COMMAND = /^\s*\/?(clear|clear chat|clear all|reset|new chat)\s*$/i;

/** Stable empty transcript, so a project with no chat yet does not churn hook deps. */
const EMPTY_CHAT: LocationChatMessage[] = [];

export default function LocationManagementPage({
  titleOverride,
  headerRight,
}: LocationManagementPageProps) {
  const projectId = getProjectId() || '';
  const featureTips = useFeatureTipsOptional();
  // Location chat. Pending proposals must live here, not in the drawer: the page derives the
  // preview tree from them.
  const [chatOpen, setChatOpen] = useState(false);

  const [chatPendingUser, setChatPendingUser] = useState<string | null>(null);
  const [chatSending, setChatSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatPending, setChatPending] = useState<LocationChatProposedAction[]>([]);

  const { width: viewportWidth } = useViewportSize();
  const chatWidth = resolveChatWidth(viewportWidth);
  // Only slide the workspace aside when there is room for both; on a narrow window the drawer
  // overlays instead, because a 300px-wide tree is worse than a covered one.
  const insetForChat = chatOpen && viewportWidth >= LOCATION_CHAT_INSET_MIN_VIEWPORT;

  const canLocationCreate = useCanSchedulerAction('locationCreate');
  const canLocationEdit = useCanSchedulerAction('locationEdit');
  const canLocationDelete = useCanSchedulerAction('locationDelete');
  const fetchTreeIfNeeded = useLocationsTreeStore((s) => s.fetchTreeIfNeeded);
  const clearTree = useLocationsTreeStore((s) => s.clearTree);

  /** Standalone Location Management page only — not when embedded in Import wizard. */
  useEffect(() => {
    if (titleOverride) return;
    featureTips?.maybeShowTip('location-management');
  }, [titleOverride, featureTips?.maybeShowTip]);

  const [treeData, setTreeData] = useState<LocationTreeNode[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [readinessTasks, setReadinessTasks] = useState<TaskSnapshot[]>([]);
  const [readinessTasksReady, setReadinessTasksReady] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setReadinessTasks([]);
      setReadinessTasksReady(true);
      return;
    }
    let cancelled = false;
    setReadinessTasksReady(false);
    void (async () => {
      try {
        const cached = getDashboardMemoryCache(projectId);
        if (cached?.tasks?.length) {
          if (!cancelled) setReadinessTasks(prepareDashboardData(cached.tasks, cached.links).tasks);
          return;
        }
        const fromDb = await loadTasksFromDB(projectId);
        if (fromDb?.tasks?.length) {
          const prepared = prepareDashboardData(fromDb.tasks, fromDb.links ?? []);
          setDashboardMemoryCache(projectId, prepared);
          if (!cancelled) setReadinessTasks(prepared.tasks);
          return;
        }
        const res = await fetchAllTasks({ projectId });
        const raw = Array.isArray((res as { data?: unknown })?.data)
          ? (res as { data: unknown[] }).data
          : [];
        const prepared = prepareDashboardData(raw, []);
        setDashboardMemoryCache(projectId, prepared);
        if (!cancelled) setReadinessTasks(prepared.tasks);
      } catch {
        if (!cancelled) setReadinessTasks([]);
      } finally {
        if (!cancelled) setReadinessTasksReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, loaded]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<LocationTreeNode[]>([]);
  const selected = selectedNodes[0] ?? null;
  const selectedParentId = selected?.parent_location_id ?? null;
  const [ensuringRoot, setEnsuringRoot] = useState(false);
  const [locationDraftApplying, setLocationDraftApplying] = useState(false);
  const [locationDiscardConfirmOpen, setLocationDiscardConfirmOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveSelectionSnapshot, setMoveSelectionSnapshot] = useState<string[]>([]);
  const [locationClipboard, setLocationClipboard] = useState<LocationTreeNode | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [shortcutsExpanded, setShortcutsExpanded] = useState(false);
  const [locationsTreeExpanded, setLocationsTreeExpanded] = useState(true);

  const loadClassificationIntoDraft = useLocationsTreeStore((s) => s.loadClassificationIntoDraft);
  const chatHistory = useLocationsTreeStore((s) => s.locationChatByProjectId[projectId]) ?? EMPTY_CHAT;
  // First name only - the panel greets, it does not address formally.
  const chatGreetingName = useMemo(
    () => getPortalSessionSnapshot()?.userName?.trim().split(/\s+/)[0] || undefined,
    [],
  );
  const storeChat = useLocationsTreeStore((s) => s.setLocationChat);
  const setChatHistory = useCallback(
    (update: (prev: LocationChatMessage[]) => LocationChatMessage[]) => {
      const current = useLocationsTreeStore.getState().locationChatByProjectId[projectId] ?? EMPTY_CHAT;
      storeChat(projectId, update(current));
    },
    [projectId, storeChat],
  );
  const moveLocationDraftSiblingsWithMerge = useLocationsTreeStore(
    (s) => s.moveLocationDraftSiblingsWithMerge,
  );
  const clearLocationDraft = useLocationsTreeStore((s) => s.clearLocationDraft);
  const addLocationDraftRemovedIds = useLocationsTreeStore((s) => s.addLocationDraftRemovedIds);
  const moveLocationDraftNode = useLocationsTreeStore((s) => s.moveLocationDraftNode);
  const addLocationDraftNode = useLocationsTreeStore((s) => s.addLocationDraftNode);
  const applyLocationDraftOps = useLocationsTreeStore((s) => s.applyLocationDraftOps);
  const updateLocationDraftNode = useLocationsTreeStore((s) => s.updateLocationDraftNode);
  const updateLocationDraftNodesByName = useLocationsTreeStore((s) => s.updateLocationDraftNodesByName);
  const cloneLocationDraftNode = useLocationsTreeStore((s) => s.cloneLocationDraftNode);
  const pasteLocationDraftSubtree = useLocationsTreeStore((s) => s.pasteLocationDraftSubtree);
  const undoLocationDraftStep = useLocationsTreeStore((s) => s.undoLocationDraftStep);
  const redoLocationDraftStep = useLocationsTreeStore((s) => s.redoLocationDraftStep);
  /** Select raw AI review state only (stable reference until store updates). */
  const locationDraft = useLocationsTreeStore((s) => s.locationDraftByProjectId[projectId] ?? null);
  const locationDraftHistory = useLocationsTreeStore((s) => s.locationDraftHistoryByProjectId[projectId]);
  const hasLocationDraft = locationDraft != null;
  const highlightedLocationIds = useMemo(() => {
    return new Set(
      selectedNodes
        .map((n) => (n.location_id != null ? String(n.location_id) : ''))
        .filter(Boolean)
    );
  }, [selectedNodes]);
  /** Effective tree for normal in-memory draft (batch save). */
  const effectiveDraftTree = useMemo(() => {
    if (!locationDraft?.locationHierarchy?.length) return [];
    const removedSet = new Set(locationDraft.removedIds);
    return filterTreeRemoved(locationDraft.locationHierarchy, removedSet);
  }, [locationDraft?.locationHierarchy, locationDraft?.removedIds]);
  const displayTree = hasLocationDraft ? effectiveDraftTree : treeData;
  /** Tree the page renders: draft/AI/saved data narrowed by the active type pill. */
  // Derived, never snapshotted: if the user edits the tree by hand while a proposal is pending,
  // this re-plans and anything stale lands in `skipped` instead of being applied silently.
  const chatPlan = useMemo(
    () => (chatPending.length > 0 ? planChatApply(chatPending, displayTree) : null),
    [chatPending, displayTree],
  );
  // Rebuilt as the tree changes, so a suggestion always names something that is really there.
  const chatSuggestions = useMemo(() => buildChatSuggestions(displayTree), [displayTree]);
  const previewTree = useMemo(
    () => (chatPlan ? buildChatPreviewTree(displayTree, chatPlan.ops) : displayTree),
    [chatPlan, displayTree],
  );
  const treeForView = useMemo(
    () => filterTreeByLocationType(previewTree, typeFilter),
    [previewTree, typeFilter],
  );
  const treeDataOverride = loaded ? treeForView : null;
  const totalLocations = useMemo(() => countNodes(displayTree), [displayTree]);
  const typePills = useMemo(() => buildTypePillCounts(displayTree), [displayTree]);
  const locationTypeOptions = useMemo(() => {
    const types = collectLocationTypes(displayTree);
    if (!types.includes('project')) types.unshift('project');
    if (!types.includes('general')) types.push('general');
    return [...new Set(types)].sort().map((t) => ({ value: t, label: formatLocationTypeLabel(t) }));
  }, [displayTree]);

  const treeContainerRef = useRef<HTMLDivElement>(null);
  const treePaneRef = useRef<HTMLDivElement>(null);
  const detailsPanelRef = useRef<HTMLDivElement>(null);
  const locationTreeApiRef = useRef<TreeApi<LocationTreeNode> | null>(null);
  const suppressNextTreeMoveRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const ensureRootAttemptedRef = useRef<string | null>(null);
  const [treeHeight, setTreeHeight] = useState(400);

  useEffect(() => {
    if (!projectId) return;
    fetchTreeIfNeeded(projectId)
      .then((data) => {
        setTreeData(data);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [projectId, fetchTreeIfNeeded]);

  /**
   * A hierarchy always needs a project root before anything can be nested under it,
   * so create one silently instead of asking for a name we already know.
   */
  useEffect(() => {
    if (!projectId || !loaded || !canLocationCreate) return;
    if (hasRootNode(treeData)) return;
    if (ensureRootAttemptedRef.current === projectId) return;
    ensureRootAttemptedRef.current = projectId;
    let cancelled = false;
    setEnsuringRoot(true);
    (async () => {
      try {
        await updateLocation([
          {
            id: crypto.randomUUID(),
            projectId,
            name: getProjectDisplayName() || 'Project',
            locationType: 'project',
            locationLevel: 1,
            parentLocationId: null,
            locationGroup: [],
          },
        ]);
        await clearTree(projectId);
        const data = await fetchTreeIfNeeded(projectId);
        if (!cancelled) setTreeData(data);
      } catch {
        // Error surfaced by the API layer.
      } finally {
        if (!cancelled) setEnsuringRoot(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, loaded, canLocationCreate, treeData, clearTree, fetchTreeIfNeeded]);

  const handleDataLoad = useCallback((data: LocationTreeNode[]) => {
    setTreeData(data);
  }, []);

  const handleSelectionCleared = useCallback(() => {
    setSelectedNodes([]);
    setSelectedId(null);
    locationTreeApiRef.current?.deselectAll();
  }, []);

  /** Move selection to the node above `removed` (or clear) when the current selection is removed. */
  const selectPreviousAfterRemoving = useCallback(
    (tree: LocationTreeNode[], removed: LocationTreeNode) => {
      const removedId = removed.location_id != null ? String(removed.location_id) : '';
      if (!removedId) {
        handleSelectionCleared();
        return;
      }
      const removedBranchIds = collectDescendantIds([removed]);
      removedBranchIds.add(removedId);
      if (selectedId == null || !removedBranchIds.has(String(selectedId))) {
        return;
      }
      const previous = findImmediatePreviousNode(tree, removedId);
      if (previous?.location_id != null) {
        const id = String(previous.location_id);
        setSelectedId(id);
        setSelectedNodes([previous]);
        locationTreeApiRef.current?.select(id, { focus: false });
      } else {
        handleSelectionCleared();
      }
    },
    [selectedId, handleSelectionCleared],
  );

  const revealNewLocationNode = useCallback(
    (nodeId: string) => {
      requestAnimationFrame(() => {
        const tree = useLocationsTreeStore.getState().getLocationDraftEffectiveTree(projectId) ?? [];
        const node = findNodeById(tree, nodeId);
        setSelectedId(nodeId);
        if (node) setSelectedNodes([node]);
        locationTreeApiRef.current?.deselectAll();
        locationTreeApiRef.current?.select(nodeId, { focus: false });
        void revealLocationInTree(locationTreeApiRef.current, tree, nodeId);
      });
    },
    [projectId],
  );

  const handleExpandAllLocations = useCallback(() => {
    locationTreeApiRef.current?.openAll();
    setLocationsTreeExpanded(true);
  }, []);

  const handleCollapseAllLocations = useCallback(() => {
    locationTreeApiRef.current?.closeAll();
    setLocationsTreeExpanded(false);
  }, []);

  const handleToggleExpandCollapseLocations = useCallback(() => {
    if (locationsTreeExpanded) {
      handleCollapseAllLocations();
    } else {
      handleExpandAllLocations();
    }
  }, [locationsTreeExpanded, handleCollapseAllLocations, handleExpandAllLocations]);

  const handleSelectionChange = useCallback(
    (nodes: LocationTreeNode[]) => {
      if (!nodes.length) {
        setSelectedNodes([]);
        setSelectedId(null);
        return;
      }
      // In AI review mode, enforce sibling-only semantics for our selectedNodes state.
      // React-arborist may allow cross-parent selection, but we normalize here.
      const parentId = nodes[0].parent_location_id ?? null;
      const siblingGroup = nodes.filter(
        (n) => (n.parent_location_id ?? null) === parentId
      );
      setSelectedNodes(siblingGroup);
      const id = siblingGroup[0]?.location_id;
      setSelectedId(id != null ? String(id) : null);
    },
    []
  );

  const refreshTreeFromStore = useCallback(async () => {
    const data = await fetchTreeIfNeeded(projectId);
    setTreeData(data);
  }, [projectId, fetchTreeIfNeeded]);

  const handleConfirmDiscardLocationDraft = useCallback(async () => {
    clearLocationDraft(projectId);
    setLocationDiscardConfirmOpen(false);
    handleSelectionCleared();
    await refreshTreeFromStore();
  }, [projectId, clearLocationDraft, handleSelectionCleared, refreshTreeFromStore]);

  const handleConfirmLeaveDiscardDraft = useCallback(async () => {
    clearLocationDraft(projectId);
    handleSelectionCleared();
    await refreshTreeFromStore();
  }, [projectId, clearLocationDraft, handleSelectionCleared, refreshTreeFromStore]);

  const leaveBlocker = useLocationDraftLeaveGuard(hasLocationDraft);

  const handleAddLocationDraft = useCallback(
    async ({
      name,
      locationType,
      aliases,
      parent,
      selectAfterAdd = true,
    }: {
      name: string;
      locationType: string;
      aliases: string[];
      parent: LocationTreeNode;
      selectAfterAdd?: boolean;
    }) => {
      if (!projectId) return;
      const newNode: LocationTreeNode = {
        location_id: crypto.randomUUID(),
        name,
        location_type: resolveLocationTypeInput(locationType || 'general') || 'general',
        location_level: (parent.location_level ?? 1) + 1,
        location_group: aliases,
        parent_location_id: parent.location_id ?? null,
        meta: {},
        child: [],
      };
      addLocationDraftNode(projectId, parent.location_id ?? null, newNode);
      if (selectAfterAdd) {
        revealNewLocationNode(String(newNode.location_id));
      } else {
        requestAnimationFrame(() => {
          const tree =
            useLocationsTreeStore.getState().getLocationDraftEffectiveTree(projectId) ?? [];
          revealLocationInTree(
            locationTreeApiRef.current,
            tree,
            String(parent.location_id ?? ''),
          );
        });
      }
      notifications.show({
        message: `Added "${name}" to draft. Save changes to persist.`,
        color: 'teal',
      });
    },
    [projectId, addLocationDraftNode, revealNewLocationNode],
  );

  const handleApplyNodeDraftUpdate = useCallback(
    ({
      nodeId,
      name,
      locationType,
      aliases,
      meta,
    }: {
      nodeId: string;
      name: string;
      locationType: string;
      aliases: string[];
      originalName: string;
      meta?: Record<string, unknown>;
    }) => {
      updateLocationDraftNode(projectId, nodeId, {
        name,
        location_type: locationType,
        location_group: aliases,
        ...(meta !== undefined ? { meta } : {}),
      });
    },
    [projectId, updateLocationDraftNode],
  );

  const handleDraftUpdateAllByNameType = useCallback(
    (name: string, locationType: string) => {
      updateLocationDraftNodesByName(projectId, name, {
        location_type: resolveLocationTypeInput(locationType) || locationType.trim() || 'general',
      });
    },
    [projectId, updateLocationDraftNodesByName],
  );

  const handleDraftRemoveByIds = useCallback(
    (ids: string[]) => {
      addLocationDraftRemovedIds(projectId, ids);
    },
    [projectId, addLocationDraftRemovedIds],
  );

  const handleDraftCloneNode = useCallback(
    (nodeId: string) => {
      cloneLocationDraftNode(projectId, nodeId);
    },
    [projectId, cloneLocationDraftNode],
  );

  /** Nodes with the same name as selected (case-sensitive); used for "update type for all occurrences". */
  const sameNameNodes = useMemo(() => {
    const name = selected?.name;
    if (name == null || name === '') return [];
    const tree = displayTree;
    return collectNodesByName(tree, name);
  }, [selected?.name, displayTree]);
  const sameNameCount = sameNameNodes.length;

  /** Delete every location sharing this name, each with its descendants. */
  const handleDeleteAllOccurrences = useCallback(() => {
    if (sameNameNodes.length === 0) return;
    const idsToRemove: string[] = [];
    for (const node of sameNameNodes) {
      idsToRemove.push(String(node.location_id));
      idsToRemove.push(...collectDescendantIds([node]));
    }
    addLocationDraftRemovedIds(projectId, [...new Set(idsToRemove)]);
    notifications.show({
      message: `Removed ${sameNameCount} occurrence(s). Press Save to persist.`,
      color: 'teal',
    });
  }, [projectId, sameNameNodes, sameNameCount, addLocationDraftRemovedIds]);

  /**
   * Derive a hierarchy from the project's schedule tasks and load it into the draft.
   *
   * The result goes through the same draft the chat and hand edits use, so it gets the same
   * preview, the same undo, and the same Save. Only `location_hierarchy` is used: task assignments
   * are re-derived server-side at Save, against the tree as actually saved, which is more reliable
   * than carrying ids through a draft the user has been rearranging.
   */
  const runClassificationIntoDraft = useCallback(
    async (mode: 'replace' | 'merge') => {
      const res = await locationClassification({ projectId });
      const derived = res.location_hierarchy ?? [];
      if (!derived.length) {
        return 'I read the schedule but could not find anything that looked like a location.';
      }
      loadClassificationIntoDraft(projectId, derived, mode);
      const added = countTreeNodes(derived);
      return mode === 'replace'
        ? `Built ${added} location(s) from the schedule, replacing what was there. Review them, then press Save.`
        : `Built ${added} location(s) from the schedule and merged them in. Review them, then press Save.`;
    },
    [projectId, loadClassificationIntoDraft],
  );

  const handleChatSend = useCallback(
    async (text: string) => {
      if (!projectId) {
        setChatError('No project selected.');
        return;
      }
      // Handled here rather than sent: the transcript is the client's, and asking the model to
      // forget costs a round trip and cannot actually erase what is on screen. Any pending
      // proposal goes with it - it belongs to the conversation being cleared - but the location
      // draft does not, since staged edits are work the user may still want to save.
      if (CLEAR_CHAT_COMMAND.test(text)) {
        setChatHistory(() => []);
        setChatPending([]);
        setChatError(null);
        return;
      }
      setChatSending(true);
      setChatError(null);
      setChatPending([]);
      setChatPendingUser(text);
      try {
        // Always send the tree on screen: with unsaved draft edits the saved tree the server would
        // otherwise load is not what the user is looking at.
        const locations = flattenTreeToUpdatePayloads(displayTree, projectId).map(
          ({ id, projectId: pid, name, locationType, locationLevel, parentLocationId, locationGroup }) => ({
            id,
            projectId: pid,
            name,
            locationType,
            locationLevel,
            parentLocationId,
            aliases: Array.isArray(locationGroup) ? locationGroup : [],
          }),
        );
        const res = await locationChat({ projectId, message: text, history: chatHistory, locations });
        // Append rather than adopting res.history: the server trims history to the last few turns
        // before sending it to the model, and echoing that truncated window back as the transcript
        // made the user's older messages disappear from the panel. The model's context bound is not
        // the display bound.
        setChatHistory((prev) => [
          ...prev,
          { role: 'user' as const, content: text },
          { role: 'assistant' as const, content: res.reply },
        ]);
        setChatPending(res.proposedActions);
        // The turn's own bubble is committed to history now, so drop the optimistic copy before
        // the classification step below - otherwise the message sits on screen twice for a minute.
        setChatPendingUser(null);

        // Runs inside the same turn so the panel keeps showing "Thinking...", but as a separate
        // request: classification reads every task in the project and can take a minute, which is
        // far longer than a chat turn should hold open.
        if (res.classifyRequest) {
          const outcome = await runClassificationIntoDraft(res.classifyRequest.mode);
          setChatHistory((prev) => [...prev, { role: 'assistant' as const, content: outcome }]);
        }
      } catch (err) {
        // Error toast shown by API
        setChatError(
          axios.isAxiosError(err) && err.response?.status === 403
            ? "You don't have access to the location assistant on this project."
            : "Couldn't reach the assistant. Try again.",
        );
      } finally {
        setChatPendingUser(null);
        setChatSending(false);
      }
    },
    [projectId, chatHistory, displayTree, runClassificationIntoDraft, setChatHistory],
  );

  // A proposal is only reviewable if the user can see it: arborist virtualizes rows, so a node
  // under a collapsed or off-screen parent renders nothing to highlight.
  useEffect(() => {
    if (!chatPlan) return;
    const firstMarked = chatPlan.marks.keys().next();
    if (firstMarked.done) return;
    void revealLocationInTree(locationTreeApiRef.current, previewTree, firstMarked.value);
  }, [chatPlan, previewTree]);

  const handleChatConfirm = useCallback(() => {
    if (!chatPlan || chatPlan.ops.length === 0) return;
    applyLocationDraftOps(projectId, chatPlan.ops);
    setChatPending([]);
    notifications.show({
      title: 'Changes staged',
      message: 'Review them in the hierarchy, then press Save to persist.',
      color: 'teal',
    });
  }, [chatPlan, applyLocationDraftOps, projectId]);

  const handleChatDiscard = useCallback(() => {
    setChatPending([]);
    setChatHistory((prev) => [
      ...prev,
      { role: 'assistant' as const, content: 'Discarded - nothing was changed.' },
    ]);
  }, [setChatHistory]);



  useEffect(() => {
    if (!hasLocationDraft || !selectedId) return;
    const node = findNodeById(effectiveDraftTree, selectedId);
    if (!node) {
      handleSelectionCleared();
      return;
    }
    setSelectedNodes((prev) => {
      const prevId = prev[0]?.location_id != null ? String(prev[0].location_id) : '';
      if (
        prevId === String(node.location_id) &&
        prev[0]?.name === node.name &&
        prev[0]?.location_type === node.location_type
      ) {
        return prev;
      }
      return [node];
    });
  }, [hasLocationDraft, effectiveDraftTree, selectedId, locationDraft, handleSelectionCleared]);

  /** Persist location draft: delete removed locations, update hierarchy, then clear draft. */
  const handleApplyLocationDraft = useCallback(async () => {
    if (!locationDraft || locationDraftApplying) return;
    setLocationDraftApplying(true);
    try {
      const removedSet = new Set(locationDraft.removedIds.map((id) => String(id)));
      const idsToDelete = getIdsSortedByDepthDescending(locationDraft.locationHierarchy, removedSet);
      if (idsToDelete.length > 0) {
        await deleteLocation({ projectId, locationIds: idsToDelete });
      }
      if (locationDraft.fromClassification) {
        // The same upsert, plus deterministic task-to-location matching against the tree as it
        // ends up saved. Carrying the classifier's own location_ids through the draft would go
        // stale the moment the user renamed or re-parented anything.
        await reassignLocationToTask({
          projectId,
          location_hierarchy: effectiveDraftTree,
          tasks: [],
        });
      } else {
        const locationPayloads = flattenTreeToUpdatePayloads(effectiveDraftTree, projectId);
        if (locationPayloads.length > 0) {
          await updateLocation(locationPayloads);
        }
      }
      clearLocationDraft(projectId);
      await clearTree(projectId);
      const data = await fetchTreeIfNeeded(projectId);
      setTreeData(data);
      notifications.show({
        title: 'Saved',
        message: locationDraft.fromClassification
          ? 'Locations saved and tasks matched to them. Refresh the Gantt to see updates.'
          : 'Location hierarchy has been saved.',
        color: 'teal',
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save location changes',
        color: 'red',
      });
    } finally {
      setLocationDraftApplying(false);
    }
  }, [
    projectId,
    locationDraft,
    effectiveDraftTree,
    locationDraftApplying,
    clearLocationDraft,
    clearTree,
    fetchTreeIfNeeded,
  ]);

  /** Mark this node and all its descendants as removed from the location draft. */
  const handleRemoveDraftNode = useCallback(
    (node: LocationTreeNode) => {
      selectPreviousAfterRemoving(displayTree, node);
      const ids = collectDescendantIds([node]);
      ids.add(String(node.location_id));
      addLocationDraftRemovedIds(projectId, Array.from(ids));
    },
    [projectId, addLocationDraftRemovedIds, displayTree, selectPreviousAfterRemoving],
  );

  const selectedDraftNode = useMemo(
    () => (selectedId && hasLocationDraft ? findNodeById(effectiveDraftTree, selectedId) : null),
    [hasLocationDraft, effectiveDraftTree, selectedId]
  );
  const selectedForPanel = hasLocationDraft ? (selectedDraftNode ?? selected) : selected;

  const handleCopyLocationDraft = useCallback(() => {
    if (!selectedForPanel?.location_id) return;
    const node = findNodeById(displayTree, String(selectedForPanel.location_id));
    if (!node) return;
    setLocationClipboard(snapshotLocationSubtree(node));
    notifications.show({ message: 'Location copied. Select a parent and paste.', color: 'blue' });
  }, [selectedForPanel, displayTree]);

  const handlePasteLocationDraft = useCallback(() => {
    if (!locationClipboard || !selectedForPanel?.location_id || !projectId) return;
    const parentId = String(selectedForPanel.location_id);
    const clipboardRootId =
      locationClipboard.location_id != null ? String(locationClipboard.location_id) : '';
    if (clipboardRootId && parentId === clipboardRootId) {
      notifications.show({ message: 'Cannot paste a location into itself.', color: 'red' });
      return;
    }
    const clipboardDescendants = collectDescendantIds([locationClipboard]);
    if (clipboardDescendants.has(parentId)) {
      notifications.show({
        message: 'Cannot paste a location into its own descendants.',
        color: 'red',
      });
      return;
    }
    const pasted = pasteLocationDraftSubtree(projectId, parentId, locationClipboard);
    if (!pasted?.location_id) return;
    revealNewLocationNode(String(pasted.location_id));
    notifications.show({
      message: `Pasted "${pasted.name ?? 'location'}" under "${selectedForPanel.name ?? 'parent'}".`,
      color: 'teal',
    });
  }, [
    locationClipboard,
    selectedForPanel,
    projectId,
    pasteLocationDraftSubtree,
    revealNewLocationNode,
  ]);

  const canUndo = hasLocationDraft && Boolean(locationDraftHistory?.past?.length);
  const canRedo = hasLocationDraft && Boolean(locationDraftHistory?.future?.length);

  const handleUndo = useCallback(() => {
    if (hasLocationDraft) undoLocationDraftStep(projectId);
  }, [hasLocationDraft, projectId, undoLocationDraftStep]);

  const handleRedo = useCallback(() => {
    if (hasLocationDraft) redoLocationDraftStep(projectId);
  }, [hasLocationDraft, projectId, redoLocationDraftStep]);

  /** Add a child under `parent` with placeholder details; refine it in the details panel. */
  const handleAddChildNode = useCallback(
    (parent: LocationTreeNode, options?: { selectAfterAdd?: boolean; name?: string; locationType?: string }) => {
      if (!canLocationCreate || !parent) return;
      const locationType = options?.locationType || 'general';
      const placement = canPlaceLocationType({
        childType: locationType,
        parent,
        tree: displayTree,
      });
      if (!placement.ok) {
        notifications.show({ message: placement.reason, color: 'red' });
        return;
      }
      const payload = {
        name: options?.name?.trim() || 'New location',
        locationType,
        aliases: [] as string[],
        parent,
        selectAfterAdd: options?.selectAfterAdd ?? false,
      };
      void handleAddLocationDraft(payload);
    },
    [canLocationCreate, handleAddLocationDraft, displayTree],
  );

  const handleTreeMove = useCallback(
    (params: { dragIds: string[]; parentId: string | null; index: number }) => {
      if (suppressNextTreeMoveRef.current) {
        suppressNextTreeMoveRef.current = false;
        return;
      }
      const parent =
        params.parentId != null ? findNodeById(displayTree, String(params.parentId)) : null;
      for (const id of params.dragIds) {
        const node = findNodeById(displayTree, String(id));
        if (!node) continue;
        if (isProjectRootNode(node)) {
          notifications.show({
            message: 'The project root cannot be moved.',
            color: 'yellow',
          });
          return;
        }
        const placement = canPlaceLocationType({
          childType: node.location_type,
          parent,
          tree: displayTree,
          excludeNodeId: String(id),
        });
        if (!placement.ok) {
          notifications.show({ message: placement.reason, color: 'red' });
          return;
        }
      }
      moveLocationDraftNode(projectId, params.dragIds, params.parentId, params.index);
      
    },
    [displayTree, projectId, moveLocationDraftNode],
  );

  const handleConfirmInlineAddChild = useCallback(
    ({
      parent,
      name,
      locationType,
    }: {
      parent: LocationTreeNode;
      name: string;
      locationType: string;
    }) => {
      handleAddChildNode(parent, {
        name,
        locationType,
        selectAfterAdd: true,
      });
      setInlineAdd(null);
    },
    [handleAddChildNode],
  );

  const projectRootNode = useMemo(() => findProjectRoot(displayTree), [displayTree]);

  const [pendingAddParentId, setPendingAddParentId] = useState<string | null>(null);
  const [inlineAdd, setInlineAdd] = useState<{
    parentId: string;
    name: string;
    locationType: string;
  } | null>(null);

  const inlineAddParent = useMemo(
    () => (inlineAdd ? findNodeById(displayTree, inlineAdd.parentId) : null),
    [inlineAdd, displayTree],
  );

  const handleCloseDetailsPanel = useCallback(() => {
    setInlineAdd(null);
    handleSelectionCleared();
  }, [handleSelectionCleared]);

  const handleAddLocationsFromToolbar = useCallback(() => {
    const parent = selectedForPanel ?? projectRootNode;
    if (!parent?.location_id) {
      notifications.show({
        message: 'No project root yet. Wait for the project location to be created.',
        color: 'yellow',
      });
      return;
    }
    setPendingAddParentId(String(parent.location_id));
  }, [selectedForPanel, projectRootNode]);

  const handleRenameNode = useCallback(
    ({ id, name }: { id: string; name: string }) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const existing = findNodeById(displayTree, id);
      const formerName = (existing?.name ?? '').trim();
      const nextAliases = appendFormerNameAsAlias(
        existing?.location_group,
        formerName,
        trimmed,
      );
      const updates = {
        name: trimmed,
        location_group: nextAliases,
      };
      updateLocationDraftNode(projectId, id, updates);
    },
    [projectId, updateLocationDraftNode, displayTree],
  );

  const handleChangeNodeType = useCallback(
    (node: LocationTreeNode, locationType: string) => {
      if (isProjectRootNode(node) || node.location_id == null) return;
      const id = String(node.location_id);
      const type = resolveLocationTypeInput(locationType || 'general') || 'general';
      const parent =
        node.parent_location_id != null
          ? findNodeById(displayTree, String(node.parent_location_id))
          : null;
      const placement = canPlaceLocationType({
        childType: type,
        parent,
        tree: displayTree,
        excludeNodeId: id,
      });
      if (!placement.ok) {
        notifications.show({ message: placement.reason, color: 'red' });
        return;
      }
      const meta = isFloorLocationType(type)
        ? node.meta
        : withFloorNumberMeta(node.meta, null);
      updateLocationDraftNode(projectId, id, {
        location_type: type,
        ...(meta ? { meta } : {}),
      });
      
    },
    [projectId, updateLocationDraftNode, displayTree],
  );

  const handleRemoveTreeNode = useCallback(
    (node: LocationTreeNode) => {
      if (!canDeleteLocationNode(node, displayTree)) {
        notifications.show({
          message:
            String(node.location_type || '').toLowerCase() === 'project'
              ? 'The only Project location cannot be removed.'
              : 'The project root cannot be removed.',
          color: 'yellow',
        });
        return;
      }
      handleRemoveDraftNode(node);
    },
    [handleRemoveDraftNode, displayTree],
  );

  /** Tab: nest selection under the previous sibling. */
  const handleIndentSelected = useCallback(() => {
    if (!canLocationEdit || !selectedId) return;
    const ctx = findNodeSiblingContext(displayTree, selectedId);
    if (!ctx || isProjectRootNode(ctx.node) || ctx.index <= 0) return;
    const prev = ctx.siblings[ctx.index - 1];
    const newParentId = prev.location_id != null ? String(prev.location_id) : null;
    if (!newParentId) return;
    const insertIndex = (prev.child ?? []).filter(
      (c) => c.location_id != null && !String(c.location_id).startsWith('__'),
    ).length;
    handleTreeMove({ dragIds: [selectedId], parentId: newParentId, index: insertIndex });
    requestAnimationFrame(() => {
      try {
        locationTreeApiRef.current?.open(newParentId);
      } catch {
        /* ignore */
      }
      const tree =
        useLocationsTreeStore.getState().getLocationDraftEffectiveTree(projectId) ?? displayTree;
      revealLocationInTree(locationTreeApiRef.current, tree, selectedId);
      locationTreeApiRef.current?.select(selectedId, { focus: true });
    });
  }, [
    canLocationEdit,
    selectedId,
    displayTree,
    projectId,
    handleTreeMove,
  ]);

  /** ⇧ Tab: outdent selection one level (become next sibling of current parent). */
  const handleOutdentSelected = useCallback(() => {
    if (!canLocationEdit || !selectedId) return;
    const ctx = findNodeSiblingContext(displayTree, selectedId);
    if (!ctx || isProjectRootNode(ctx.node) || ctx.parentId == null) return;
    const parentCtx = findNodeSiblingContext(displayTree, ctx.parentId);
    if (!parentCtx) return;
    const newParentId = parentCtx.parentId;
    const insertIndex = parentCtx.index + 1;
    handleTreeMove({ dragIds: [selectedId], parentId: newParentId, index: insertIndex });
    requestAnimationFrame(() => {
      const tree =
        useLocationsTreeStore.getState().getLocationDraftEffectiveTree(projectId) ?? displayTree;
      revealLocationInTree(locationTreeApiRef.current, tree, selectedId);
      locationTreeApiRef.current?.select(selectedId, { focus: true });
    });
  }, [
    canLocationEdit,
    selectedId,
    displayTree,
    projectId,
    handleTreeMove,
  ]);

  /** Delete / Backspace: remove the current selection (not the project root). */
  const handleDeleteSelected = useCallback(() => {
    if (!canLocationDelete) return;
    const primary =
      selectedNodes[0] ??
      (selectedId ? findNodeById(displayTree, selectedId) : null);
    if (!primary) return;
    handleRemoveTreeNode(primary);
  }, [canLocationDelete, selectedNodes, selectedId, displayTree, handleRemoveTreeNode]);

  const handleDuplicateTreeNode = useCallback(
    (node: LocationTreeNode) => {
      if (!canLocationCreate || node.location_id == null) return;
      if (isProjectRootNode(node)) return;
      handleDraftCloneNode(String(node.location_id));
    },
    [canLocationCreate, handleDraftCloneNode],
  );

  const handleDuplicateSelected = useCallback(() => {
    if (!selectedForPanel) return;
    handleDuplicateTreeNode(selectedForPanel);
  }, [selectedForPanel, handleDuplicateTreeNode]);

  const handleGenerateChildren = useCallback(
    ({
      parent,
      locationType,
      names,
    }: {
      parent: LocationTreeNode;
      locationType: string;
      names: string[];
    }) => {
      if (!projectId || !canLocationCreate || names.length === 0) return;
      const parentId = parent.location_id != null ? String(parent.location_id) : null;
      const type = resolveLocationTypeInput(locationType || 'general') || 'general';
      let lastId = '';
      for (const name of names) {
        const newNode: LocationTreeNode = {
          location_id: crypto.randomUUID(),
          name,
          location_type: type,
          location_level: (parent.location_level ?? 1) + 1,
          location_group: [],
          parent_location_id: parentId,
          meta: {},
          child: [],
        };
        lastId = String(newNode.location_id);
        addLocationDraftNode(projectId, parentId, newNode);
      }
      if (lastId) revealNewLocationNode(lastId);
      notifications.show({
        message: `Added ${names.length} location(s) to draft. Save changes to persist.`,
        color: 'teal',
      });
    },
    [
      projectId,
      canLocationCreate,
      addLocationDraftNode,
      revealNewLocationNode,
    ],
  );

  const handlePanelApplyNodeUpdate = useCallback(
    (payload: {
      nodeId: string;
      name: string;
      locationType: string;
      aliases: string[];
      originalName: string;
      meta?: Record<string, unknown>;
    }) => {
      const existing = findNodeById(displayTree, payload.nodeId);
      const typeUnchanged =
        normalizeLocationTypeKey(payload.locationType) ===
        normalizeLocationTypeKey(existing?.location_type || '');
      if (payload.meta !== undefined && typeUnchanged) {
        suppressNextTreeMoveRef.current = true;
      }
      handleApplyNodeDraftUpdate(payload);
    },
    [projectId, handleApplyNodeDraftUpdate, displayTree],
  );

  const handlePanelUpdateAllByNameType = useCallback(
    (name: string, locationType: string) => {
      handleDraftUpdateAllByNameType(name, locationType);
    },
    [projectId, handleDraftUpdateAllByNameType],
  );

  const handlePanelRemoveByIds = useCallback(
    (ids: string[]) => {
      handleDraftRemoveByIds(ids);
    },
    [projectId, handleDraftRemoveByIds],
  );

  const handlePanelCloneNode = useCallback(
    (nodeId: string) => {
      handleDraftCloneNode(nodeId);
    },
    [handleDraftCloneNode],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        Boolean(target?.isContentEditable);
      const inTree =
        Boolean(target?.closest?.('[role="tree"]')) ||
        Boolean(treePaneRef.current?.contains(target));
      const inComposer = Boolean(
        target?.closest?.(
          '.location-tree-row.is-inline-editing, .location-tree-row.is-add-composer, .location-tree-node-mgmt--composer',
        ),
      );
      const mod = e.metaKey || e.ctrlKey;

      if (!mod && !inField && !inComposer && inTree) {
        if (e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          if (e.shiftKey) handleOutdentSelected();
          else handleIndentSelected();
          return;
        }
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          e.stopPropagation();
          handleDeleteSelected();
          return;
        }
      }

      if (!mod) {
        if (inField) return;
        if (e.key === '?') {
          e.preventDefault();
          setShortcutsExpanded(true);
        } else if (e.key === '/') {
          e.preventDefault();
          searchInputRef.current?.focus();
        }
        return;
      }
      if (inField) return;
      const key = e.key.toLowerCase();
      if (key === 'c') {
        e.preventDefault();
        handleCopyLocationDraft();
      } else if (key === 'v') {
        e.preventDefault();
        handlePasteLocationDraft();
      } else if (key === 'd') {
        e.preventDefault();
        handleDuplicateSelected();
      } else if (key === 'z') {
        e.preventDefault();
        if (e.shiftKey) handleRedo();
        else handleUndo();
      }
    };
    // Capture so Tab is handled before react-arborist's default focus-next behavior.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [
    handleCopyLocationDraft,
    handlePasteLocationDraft,
    handleDuplicateSelected,
    handleUndo,
    handleRedo,
    handleIndentSelected,
    handleOutdentSelected,
    handleDeleteSelected,
  ]);

  const selectedSiblingCount = selectedNodes.length;
  const isValidSiblingSelectionForMove = useMemo(() => {
    // At least two selected nodes that share the same parent.
    if (selectedNodes.length < 2) return false;
    const parentId = selectedNodes[0].parent_location_id ?? null;
    return selectedNodes.every((n) => (n.parent_location_id ?? null) === parentId);
  }, [selectedNodes]);

  useEffect(() => {
    const el = treeContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setTreeHeight(Math.max(120, el.clientHeight));
    });
    ro.observe(el);
    setTreeHeight(Math.max(120, el.clientHeight));
    return () => ro.disconnect();
  }, [loaded, ensuringRoot]);

  // Click / focus outside the tree + details panel closes the side menu (clears selection).
  useEffect(() => {
    if (!selectedForPanel) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (treePaneRef.current?.contains(target)) return;
      if (detailsPanelRef.current?.contains(target)) return;
      // Mantine portals (Select, Modal, Tooltip, Notifications)
      if (
        target.closest(
          '[data-portal], .location-chat-drawer-root, .mantine-Modal-root, .mantine-Popover-dropdown, .mantine-Select-dropdown, .mantine-Notification-root, .mantine-Menu-dropdown',
        )
      ) {
        return;
      }
      handleSelectionCleared();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [selectedForPanel, handleSelectionCleared]);

  if (!projectId) {
    return (
      <Box p="md">
        <Text c="dimmed">No project selected. Select a project from the app settings or context.</Text>
      </Box>
    );
  }

  const canSaveDraft = canLocationEdit || canLocationCreate || canLocationDelete;
  const showTreeLoader = !loaded || ensuringRoot;
  const showEmptyState = !showTreeLoader && treeForView.length === 0;

  const readinessSuppressed =
    leaveBlocker.state === 'blocked' ||
    moveModalOpen ||
    chatOpen ||
    locationDiscardConfirmOpen;

  return (
    <Box
      className="location-mgmt-shell"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        // The drawer has no overlay and is fixed to the right edge, so without this the tree runs
        // underneath it and loses its whole type column. Inset the workspace by the drawer width
        // instead; the tree's ResizeObserver picks the new width up on its own.
        paddingRight: insetForChat ? chatWidth : 0,
        transition: 'padding-right 200ms ease',
      }}
    >
      {projectId ? (
        <ScheduleReadinessBanner
          projectId={projectId}
          tasks={readinessTasks}
          locationTree={displayTree}
          surface="locations"
          suppressed={readinessSuppressed}
          dataReady={loaded && readinessTasksReady}
          onVisibleState={(state) => {
            if (state === 'ready' || titleOverride) return;
            featureTips?.maybeShowTip('locations-and-tasks');
          }}
        />
      ) : null}

      {/* Header: title + history on the left, search + draft actions on the right */}
      <Box className="location-mgmt-header">
        <Group justify="space-between" align="center" gap="md" wrap="wrap">
          <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
            <Text className="location-mgmt-header-title">
              {titleOverride ?? 'Location management'}
            </Text>
            {hasLocationDraft && (
              <>
                <Text className="location-mgmt-header-draft-label">Draft</Text>
                <Tooltip label="Undo" withArrow>
                  <ActionIcon
                    className="location-mgmt-header-history"
                    variant="default"
                    size="sm"
                    onClick={handleUndo}
                    disabled={!canUndo}
                    aria-label="Undo"
                  >
                    <IconArrowBackUp size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Redo" withArrow>
                  <ActionIcon
                    className="location-mgmt-header-history"
                    variant="default"
                    size="sm"
                    onClick={handleRedo}
                    disabled={!canRedo}
                    aria-label="Redo"
                  >
                    <IconArrowForwardUp size={16} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            <TextInput
              className="location-mgmt-header-search"
              ref={searchInputRef}
              placeholder="Search locations..."
              size="sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ width: 220 }}
              leftSection={<IconSearch size={14} stroke={1.75} />}
              styles={{
                input: {
                  paddingTop: 8,
                  paddingBottom: 8,
                  paddingLeft: 36,
                },
                section: { color: '#8B91A0' },
              }}
              rightSection={
                searchTerm ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Clear search"
                    onClick={() => setSearchTerm('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
            />
            {hasLocationDraft && (
              <Box className="location-mgmt-header-draft-actions">
                <Button
                  className="location-mgmt-header-discard"
                  variant="default"
                  onClick={() => setLocationDiscardConfirmOpen(true)}
                >
                  Discard
                </Button>
                <PermissionRestrictedTooltip restricted={!canSaveDraft}>
                  <Button
                    className="location-mgmt-header-save"
                    onClick={() => void handleApplyLocationDraft()}
                    loading={locationDraftApplying}
                    disabled={!canSaveDraft}
                  >
                    Save
                  </Button>
                </PermissionRestrictedTooltip>
              </Box>
            )}
            <Tooltip label="Ask about locations, or build them from the schedule tasks">
              <Button
                className="location-mgmt-toolbar-ai"
                variant="default"
                onClick={() => setChatOpen(true)}
                // A spinner in the icon slot rather than `loading`, which would disable the
                // button: a classification run takes a minute and the user must still be able to
                // close the panel while it works.
                leftSection={
                  chatSending ? (
                    <Loader size={14} />
                  ) : (
                    <img
                      src={aiClassificationIcon}
                      alt=""
                      width={14}
                      height={14}
                      className="location-mgmt-toolbar-ai-icon"
                      aria-hidden
                    />
                  )
                }
              >
                AI Assistant
              </Button>
            </Tooltip>
            {headerRight}
          </Group>
        </Group>
      </Box>

      {/* Body: toolbar+tree | optional details */}
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >

        <Box
          ref={treePaneRef}
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          <Box className="location-mgmt-toolbar">
            <div className="location-mgmt-toolbar-pills">
              <Group gap={8} wrap="nowrap" style={{ width: 'max-content' }}>
                <Text className="location-mgmt-toolbar-count" component="span">
                  {totalLocations} Locations
                </Text>
                {typePills.map(({ type, count }) => (
                  <button
                    key={type}
                    type="button"
                    className="location-mgmt-type-pill"
                    data-active={typeFilter === type ? 'true' : 'false'}
                    onClick={() => setTypeFilter((prev) => (prev === type ? null : type))}
                  >
                    {formatLocationTypeLabel(type)}
                    <span className="location-mgmt-type-pill-sep" aria-hidden>
                      ·
                    </span>
                    <span className="location-mgmt-type-pill-count">{count}</span>
                  </button>
                ))}
              </Group>
            </div>
            <Group gap={8} wrap="nowrap" style={{ flexShrink: 0 }}>
              {isValidSiblingSelectionForMove && (
                <Button
                  className="location-mgmt-ai-btn location-mgmt-ai-btn--primary"
                  onClick={() => {
                    setMoveSelectionSnapshot(selectedNodes.map((n) => String(n.location_id)));
                    setMoveModalOpen(true);
                  }}
                >
                  Merge {selectedSiblingCount} selected
                </Button>
              )}
              <Button
                className="location-mgmt-toolbar-btn"
                variant="default"
                onClick={handleToggleExpandCollapseLocations}
                rightSection={
                  locationsTreeExpanded ? (
                    <IconChevronRight size={14} stroke={1.75} />
                  ) : (
                    <IconChevronDown size={14} stroke={1.75} />
                  )
                }
              >
                {locationsTreeExpanded ? 'Collapse all' : 'Expand all'}
              </Button>
              <PermissionRestrictedTooltip restricted={!canLocationCreate}>
                <Button
                  className="location-mgmt-toolbar-add"
                  variant="default"
                  leftSection={<IconPlus size={14} />}
                  onClick={handleAddLocationsFromToolbar}
                  disabled={!canLocationCreate}
                >
                  Add locations
                </Button>
              </PermissionRestrictedTooltip>
            </Group>
          </Box>

          <Box
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              padding: '8px 16px 16px',
            }}
          >
          {showTreeLoader ? (
            <Box
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              <Loader size="sm" />
              {ensuringRoot && (
                <Text size="xs" c="dimmed">
                  Setting up the project location…
                </Text>
              )}
            </Box>
          ) : (
            <>
              <Box
                ref={treeContainerRef}
                className="location-mgmt-tree"
                style={{ flex: 1, minHeight: 0, overflow: 'auto' }}
              >
                {showEmptyState ? (
                  <Stack gap={6} p="md">
                    <Text size="sm" c="dimmed">
                      {typeFilter
                        ? `No locations of type "${formatLocationTypeLabel(typeFilter)}" yet.`
                        : 'No locations yet. Add a top-level location to start the hierarchy.'}
                    </Text>
                    {typeFilter && (
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={() => setTypeFilter(null)}
                        style={{ alignSelf: 'flex-start' }}
                      >
                        Clear filter
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <LocationHierarchyTree
                    projectId={projectId}
                    treeApiRef={locationTreeApiRef}
                    variant="management"
                    onSelectionChange={handleSelectionChange}
                    onDataLoad={treeDataOverride ? undefined : handleDataLoad}
                    onRename={canLocationEdit ? handleRenameNode : undefined}
                    showRenameHint
                    selectedLocationId={null}
                    highlightedLocationIds={highlightedLocationIds}
                    activeLocationId={selectedId}
                    pendingChatMarks={chatPlan?.marks}
                    searchTerm={searchTerm}
                    width="100%"
                    height={treeHeight}
                    dataOverride={treeDataOverride}
                    disableEdit={!canLocationEdit}
                    disableDrag={!canLocationEdit}
                    onMove={canLocationEdit ? handleTreeMove : undefined}
                    onRemoveNode={handleRemoveTreeNode}
                    onAddChild={handleAddChildNode}
                    onConfirmAddChild={handleConfirmInlineAddChild}
                    pendingAddParentId={pendingAddParentId}
                    onPendingAddParentConsumed={() => setPendingAddParentId(null)}
                    inlineAdd={inlineAdd}
                    onInlineAddChange={setInlineAdd}
                    onDuplicateNode={handleDuplicateTreeNode}
                    onChangeType={handleChangeNodeType}
                    locationTypeOptions={locationTypeOptions}
                    canCreate={canLocationCreate}
                    canEdit={canLocationEdit}
                    canDelete={canLocationDelete}
                  />
                )}
              </Box>
            </>
          )}
          </Box>
        </Box>

        {(selectedForPanel || inlineAddParent) && (
          <Box
            ref={detailsPanelRef}
            style={{
              flex: '0 0 320px',
              width: 320,
              minWidth: 300,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderLeft: '1px solid var(--mantine-color-default-border)',
              backgroundColor: 'var(--mantine-color-body)',
            }}
          >
            <LocationSelectionEditPanel
              key={
                inlineAddParent && inlineAdd
                  ? `pending-add-${inlineAdd.parentId}`
                  : String(selectedForPanel!.location_id)
              }
              selected={selectedForPanel ?? undefined}
              pendingAdd={
                inlineAddParent && inlineAdd
                  ? {
                      parent: inlineAddParent,
                      name: inlineAdd.name,
                      locationType: inlineAdd.locationType,
                      onNameChange: (name) =>
                        setInlineAdd((prev) => (prev ? { ...prev, name } : null)),
                      onTypeChange: (locationType) =>
                        setInlineAdd((prev) => (prev ? { ...prev, locationType } : null)),
                      onConfirm: () => {
                        handleConfirmInlineAddChild({
                          parent: inlineAddParent,
                          name: inlineAdd.name,
                          locationType: inlineAdd.locationType,
                        });
                      },
                    }
                  : null
              }
              treeData={displayTree}
              onApplyNodeUpdate={handlePanelApplyNodeUpdate}
              onUpdateAllByNameType={handlePanelUpdateAllByNameType}
              onRemoveByIds={handlePanelRemoveByIds}
              onCloneNode={handlePanelCloneNode}
              onBeforeDelete={(node) => selectPreviousAfterRemoving(displayTree, node)}
              onCopyNode={handleCopyLocationDraft}
              onPasteNode={handlePasteLocationDraft}
              canPaste={Boolean(locationClipboard)}
              canEdit={canLocationEdit}
              canCreate={canLocationCreate}
              canDelete={canLocationDelete}
              onGenerateChildren={handleGenerateChildren}
              shortcutsExpanded={shortcutsExpanded}
              onShortcutsExpandedChange={setShortcutsExpanded}
              onClosePanel={handleCloseDetailsPanel}
              headerNotice={
                sameNameCount > 1 && selectedSiblingCount === 1 && selectedForPanel ? (
                  <Box
                    style={{
                      padding: 12,
                      borderRadius: 10,
                      border: '1px solid var(--mantine-color-default-border)',
                      background: 'var(--mantine-color-body)',
                      flexShrink: 0,
                    }}
                  >
                    <Text size="sm" fw={600} mb={4}>
                      {sameNameCount} duplicates
                    </Text>
                    <Text size="xs" c="dimmed" mb={8}>
                      Other locations share the name &quot;{selectedForPanel.name}&quot;.
                    </Text>
                    <Button
                      className="location-mgmt-ai-btn location-mgmt-ai-btn--danger"
                      variant="default"
                      onClick={handleDeleteAllOccurrences}
                    >
                      Delete all occurrences
                    </Button>
                  </Box>
                ) : null
              }
            />
          </Box>
        )}
      </Box>

      <LocationBulkMoveModal
        opened={moveModalOpen}
        projectId={projectId}
        tree={displayTree}
        currentParentId={selectedParentId}
        selectedIds={moveSelectionSnapshot}
        onClose={() => setMoveModalOpen(false)}
        onConfirm={(targetParentId) => {
          if (!projectId || moveSelectionSnapshot.length === 0) {
            setMoveModalOpen(false);
            return;
          }
          moveLocationDraftSiblingsWithMerge(projectId, moveSelectionSnapshot, targetParentId);
          setMoveModalOpen(false);
        }}
      />

      <Modal
        opened={locationDiscardConfirmOpen}
        onClose={() => setLocationDiscardConfirmOpen(false)}
        title="Discard unsaved changes?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            All location edits made since your last save will be lost. The hierarchy will revert to
            the last saved version.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setLocationDiscardConfirmOpen(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void handleConfirmDiscardLocationDraft()}>
              Discard changes
            </Button>
          </Group>
        </Stack>
      </Modal>

      <LocationDraftLeaveConfirmModal
        blocker={leaveBlocker}
        onConfirmLeave={handleConfirmLeaveDiscardDraft}
      />

      <LocationChatDrawer
        opened={chatOpen}
        onClose={() => setChatOpen(false)}
        history={chatHistory}
        pendingUserText={chatPendingUser}
        sending={chatSending}
        error={chatError}
        plan={chatPlan}
        appliedActions={[]}
        canMutate={canLocationCreate || canLocationEdit || canLocationDelete}
        width={chatWidth}
        greetingName={chatGreetingName}
        suggestions={chatSuggestions}
        onSend={(text) => void handleChatSend(text)}
        onConfirm={handleChatConfirm}
        onDiscard={handleChatDiscard}
      />
    </Box>
  );
}
